import { set } from '@ember/object';
import Service, { service } from '@ember/service';
import type StoreService from '@ember-data/store';
import { tracked } from '@glimmer/tracking';
import { restartableTask } from 'ember-concurrency';
import type DeviceModel from 'garaje/models/device';
import type DeviceStatusModel from 'garaje/models/device-status';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type MetricsService from 'garaje/services/metrics';

interface DeviceDeletedPayload {
  external_id?: string;
  device_id?: string;
  id?: string;
  product?: string;
  location_id?: string | number;
}

interface StatusBackedDeviceRemovalSnapshot {
  externalId: string;
  devicePayload: Record<string, unknown> | null;
  statusPayload: Record<string, unknown> | null;
}

export default class DeviceStatusService extends Service {
  @service declare store: StoreService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare metrics: MetricsService;

  @tracked statusByDeviceId = new Map<string, DeviceStatusModel>();
  @tracked fetchError: Error | null = null;
  @tracked lastFetchedLocationId: string | null = null;

  fetchInitialStatuses = restartableTask(async (locationId: string, product?: string) => {
    try {
      const queryParams: Record<string, string | number> = {
        location_id: locationId,
        limit: 100,
        ...(product ? { product } : {}),
      };
      const statuses = await this.store.query('device-status', queryParams);
      const newMap = new Map<string, DeviceStatusModel>();
      for (const status of statuses.slice()) {
        // eslint-disable-next-line ember/use-ember-get-and-set -- Map.set, not Ember set
        newMap.set(status.externalId, status);
      }
      this.statusByDeviceId = newMap;
      this.lastFetchedLocationId = locationId;
      this.fetchError = null;
    } catch (error) {
      this.fetchError = error as Error;
      this.metrics.logMonitorEvent('DEVICES_SERVICE_FETCH_ERROR', {
        location_id: locationId,
        error: (error as Error).message,
      });
    }
  });

  onConnectionStatusChanged(payload: {
    external_id: string;
    product: string;
    connection_status: 'CONNECTED' | 'DISCONNECTED';
    connection_status_updated_at?: string;
    telemetry?: Record<string, unknown>;
  }): void {
    // eslint-disable-next-line ember/use-ember-get-and-set -- Map.get, not Ember get
    const existing = this.statusByDeviceId.get(payload.external_id);
    if (existing) {
      set(existing, 'connectionStatus', payload.connection_status);
      if (payload.connection_status_updated_at) {
        set(existing, 'connectionStatusUpdatedAt', new Date(payload.connection_status_updated_at));
      }
      if (payload.telemetry) {
        set(existing, 'telemetry', { ...existing.telemetry, ...payload.telemetry });
      }
    } else {
      const newMap = new Map(this.statusByDeviceId);
      const partial = this.store.push({
        data: {
          id: payload.external_id,
          type: 'device-status',
          attributes: {
            externalId: payload.external_id,
            product: payload.product,
            connectionStatus: payload.connection_status,
            connectionStatusUpdatedAt: payload.connection_status_updated_at,
            telemetry: payload.telemetry,
          },
        },
      }) as DeviceStatusModel;
      // eslint-disable-next-line ember/use-ember-get-and-set -- Map.set, not Ember set
      newMap.set(payload.external_id, partial);
      this.statusByDeviceId = newMap;
    }

    this.syncTelemetryToDevice(payload.external_id, payload.telemetry);
  }

  onDeviceDeleted(payload: DeviceDeletedPayload): void {
    if (payload.product !== 'visitors') {
      return;
    }

    const externalId = payload.external_id ?? payload.device_id ?? payload.id;
    if (!externalId) {
      return;
    }

    // eslint-disable-next-line ember/use-ember-get-and-set -- Map.get, not Ember get
    const status = this.statusByDeviceId.get(externalId);
    if (payload.location_id && status?.locationId && String(status.locationId) !== String(payload.location_id)) {
      return;
    }

    this.removeStatusBackedDevice(externalId);
  }

  removeStatusBackedDevice(
    externalId: string,
    options: { locationId?: string | number | null; unloadDevice?: boolean } = {},
  ): StatusBackedDeviceRemovalSnapshot | null {
    // eslint-disable-next-line ember/use-ember-get-and-set -- Map.get, not Ember get
    const status = this.statusByDeviceId.get(externalId);
    if (options.locationId && status?.locationId && String(status.locationId) !== String(options.locationId)) {
      return null;
    }

    const device = this.store.peekRecord('device', externalId);
    const locationId = options.locationId ?? status?.locationId;
    const snapshot: StatusBackedDeviceRemovalSnapshot = {
      externalId,
      devicePayload: device ? this.devicePayload(device, locationId) : null,
      statusPayload: status ? this.statusPayload(status) : null,
    };

    status?.unloadRecord();

    const newMap = new Map(this.statusByDeviceId);
    newMap.delete(externalId);
    this.statusByDeviceId = newMap;

    // Callers with an in-flight save on the device (e.g. mid-destroyRecord) must pass
    // unloadDevice: false — unloading an in-flight record throws in Ember Data, and
    // destroyRecord unloads the record itself once the request succeeds.
    if (options.unloadDevice ?? true) {
      device?.unloadRecord();
    }

    return snapshot;
  }

  restoreStatusBackedDevice(snapshot: StatusBackedDeviceRemovalSnapshot | null): void {
    if (!snapshot) {
      return;
    }

    let restoredStatus: DeviceStatusModel | null = null;
    if (snapshot.statusPayload) {
      restoredStatus = this.store.push(snapshot.statusPayload) as DeviceStatusModel;
    }

    if (snapshot.devicePayload) {
      this.store.push(snapshot.devicePayload);
    }

    if (restoredStatus) {
      const newMap = new Map(this.statusByDeviceId);
      // eslint-disable-next-line ember/use-ember-get-and-set -- Map.set, not Ember set
      newMap.set(snapshot.externalId, restoredStatus);
      this.statusByDeviceId = newMap;
    }
  }

  getStatus(externalId: string): DeviceStatusModel | null {
    // eslint-disable-next-line ember/use-ember-get-and-set -- Map.get, not Ember get
    return this.statusByDeviceId.get(externalId) ?? null;
  }

  async ensureStatuses(locationId: string, product?: string): Promise<void> {
    const task = this.fetchInitialStatuses;
    if (task.isRunning) {
      await task.last;
      return;
    }
    if (this.lastFetchedLocationId === locationId) {
      return;
    }
    await task.perform(locationId, product);
  }

  clearStatuses(): void {
    void this.fetchInitialStatuses.cancelAll();
    this.statusByDeviceId = new Map();
    this.lastFetchedLocationId = null;
  }

  private syncTelemetryToDevice(externalId: string, telemetry?: Record<string, unknown>): void {
    if (!telemetry) {
      return;
    }

    const existingDevice = this.store.peekRecord('device', externalId);
    if (!existingDevice) {
      return;
    }

    const attributes: Record<string, unknown> = { ...telemetry };
    if ('sleeping' in telemetry) {
      attributes['is-sleeping'] = telemetry['sleeping'] ?? false;
    }

    const normalized = this.store.normalize('device', {
      type: 'devices',
      id: externalId,
      attributes,
    });

    this.store.push(normalized);
  }

  private statusPayload(status: DeviceStatusModel): Record<string, unknown> {
    return {
      data: {
        id: status.id,
        type: 'device-status',
        attributes: {
          companyId: status.companyId,
          product: status.product,
          externalId: status.externalId,
          locationId: status.locationId,
          connectionStatus: status.connectionStatus,
          connectionStatusUpdatedAt: status.connectionStatusUpdatedAt,
          telemetry: status.telemetry,
          lastEnrolledAt: status.lastEnrolledAt,
        },
      },
    };
  }

  private devicePayload(device: DeviceModel, locationId?: string | number | null): Record<string, unknown> {
    return {
      data: {
        id: device.id,
        type: 'device',
        attributes: {
          userToken: device.userToken,
          isConnected: device.isConnected,
          grantExpiresAt: device.grantExpiresAt,
          osVersion: device.osVersion,
          maximumOsVersion: device.maximumOsVersion,
          appVersion: device.appVersion,
          currentIpadAppVersion: device.currentIpadAppVersion,
          ipAddress: device.ipAddress,
          wifiNetwork: device.wifiNetwork,
          printerStatus: device.printerStatus,
          failoverPrinterStatus: device.failoverPrinterStatus,
          lastHeartbeatAt: device.lastHeartbeatAt,
          isSleeping: device.isSleeping,
          deviceType: device.deviceType,
          name: device.name,
          alias: device.alias,
        },
        ...(locationId
          ? {
              relationships: {
                location: { data: { type: 'location', id: String(locationId) } },
              },
            }
          : {}),
      },
    };
  }
}

declare module '@ember/service' {
  interface Registry {
    'device-status-service': DeviceStatusService;
  }
}
